import React, { useState, useEffect } from "react";
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  useRefresh,
  ArrayField,
  ChipField,
  SingleFieldList,
  TopToolbar,
  SimpleForm,
  TextInput,
  NumberInput,
  useNotify,
  useDataProvider,
  Button,
  Toolbar,
  SelectInput,
  ReferenceInput,
  required,
  useChoicesContext,
  Form,
  DateInput,
  DateTimeInput,
  usePermissions,
  CreateButton,
  useGetOne,
  FunctionField,
  SaveButton,
  DeleteButton,
  Title,
  useRedirect,
} from "react-admin";
import Paper from "@mui/material/Paper";
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  TextField as MuiTextField,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSearchParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useFilterContext } from "./FilterContext";
import ExportButton from "./ExportButton";
import { useMediaQuery, useTheme } from "@mui/material";
import UpgradeStickyBanner from "../utils/UpgradeStickyBanner";
import UpgradePlanModal from "../components/UpgradePlanModal";
import { TransactionEditDialog, BookingCommissionEditDialog } from "./Components";
import moment from "moment";

export const PAYMENT_METHODS = Object.freeze({
  CASH: "Efectivo",
  CARD: "Tarjeta",
  QR: "QR",
  TIGO_MONEY: "Tigo Money",
  COURTESY: "Cortesia",
  GIFT_CARD: "Gift Card",
});

const postRowStyle = (record, index) => ({
  backgroundColor: !record?.paymentInfo.isFeePaid ? "#ffdfd4" : "",
});

// Componente interno para manejar la actualización automática de los campos
const AutoUpdateFields = ({ servicesCart, calculateTotal, calculateEmployeeCommission }) => {
  const { setValue } = useFormContext();

  useEffect(() => {
    if (servicesCart.length > 0) {
      const newTotal = calculateTotal();
      const newCommission = calculateEmployeeCommission();

      setValue("totalPrice", newTotal);
      setValue("feeAmountEmployee", newCommission);
    }
  }, [servicesCart, calculateTotal, calculateEmployeeCommission, setValue]);

  return null; // Este componente no renderiza nada
};

// Componente personalizado para el selector de servicios con búsqueda
const ServiceSelector = ({ service, allServices, onServiceChange, index }) => {
  const [inputValue, setInputValue] = useState(service.name || "");
  const [open, setOpen] = useState(false);
  const [filteredServices, setFilteredServices] = useState(allServices);

  useEffect(() => {
    setInputValue(service.name || "");
  }, [service.name]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);

    // Filtrar servicios basado en el texto de búsqueda
    const filtered = allServices.filter((service) => service.name.toLowerCase().includes(value.toLowerCase()));
    setFilteredServices(filtered);
  };

  const handleServiceSelect = (selectedService) => {
    onServiceChange(selectedService, index);
    setInputValue(selectedService.name);
    setOpen(false);
  };

  return (
    <Box sx={{ position: "relative", flex: 1 }}>
      <MuiTextField
        fullWidth
        label="Servicio"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "1rem",
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          },
          "& .MuiInputLabel-root": {
            fontSize: "1rem",
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          },
        }}
      />
      {open && filteredServices.length > 0 && (
        <Paper
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 200,
            overflow: "auto",
            mt: 0.5,
          }}
        >
          {filteredServices.map((service) => (
            <MenuItem
              key={service._id}
              onClick={() => handleServiceSelect(service)}
              sx={{
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <Box>
                <Typography variant="body1">{service.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Bs.{" "}
                  {parseFloat(service.unitPrice) % 1 === 0
                    ? parseFloat(service.unitPrice).toString()
                    : parseFloat(service.unitPrice).toFixed(2)}{" "}
                  - {service.unitEstimatedWorkMinutes || 0} min
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Paper>
      )}
    </Box>
  );
};
const TransactionDialog = ({ open, handleClose, record, onDeleteSuccess, isEdit, onSuccess }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions } = usePermissions();

  const provider = JSON.parse(localStorage.getItem("profile"));
  const providerId = permissions !== "admin-sucursal" ? provider._id : provider.employer._id;

  const handleCreateSave = async (values) => {
    try {
      const { employeeData, ...restValues } = values;
      const dataToSend = {
        ...restValues,
        provider: providerId,
      };

      if (employeeData) {
        dataToSend.employee = {
          _id: employeeData._id,
          fullName: `${employeeData.first_name} ${employeeData.last_name}`,
          phone: employeeData.phone,
          photoURL: employeeData.picture,
          pushToken: employeeData.pushToken,
          CI: employeeData.CI_NIT,
          location: employeeData.location || [],
        };
      }

      await dataProvider.create("transaction", { data: dataToSend });
      notify("Transacción creada con éxito", { type: "success" });
      handleClose();
      onSuccess();
    } catch (error) {
      notify("Error al crear la transacción", { type: "error" });
    }
  };

  const handleEditSave = async (values) => {
    try {
      const dataToSend = {
        ...values,
        provider: providerId,
        employee: values.employeeData,
      };
      await dataProvider.update("transaction", { id: record._id, data: dataToSend });
      notify("Transacción actualizada con éxito", { type: "success" });
      handleClose();
      onSuccess();
    } catch (error) {
      notify("Error al actualizar la transacción", { type: "error" });
    }
  };

  const handleDelete = async () => {
    if (record) {
      try {
        await dataProvider.delete("transaction", { id: record._id });
        notify("Transacción eliminada con éxito", { type: "success" });
        redirect("/bookingBalance");
        onDeleteSuccess();
        handleClose();
      } catch (error) {
        notify("Error al eliminar la transacción", { type: "error" });
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{record?.type ? `Detalles de ${record.type}` : "Crear transacción"}</DialogTitle>
      <DialogContent>
        <SimpleForm
          record={record}
          onSubmit={isEdit ? handleEditSave : handleCreateSave}
          toolbar={
            <Toolbar>
              <SaveButton resource="transaction" />
              {isEdit && (
                <DeleteButton resource="transaction" record={record} label="Eliminar" onClick={handleDelete} />
              )}
              <Button onClick={handleClose} style={{ marginLeft: 8 }}>
                Cerrar
              </Button>
            </Toolbar>
          }
          title={<Title title="" />}
        >
          <SelectInput
            source="type"
            label="Tipo de transacción"
            choices={[
              { id: "Egreso", name: "Egreso" },
              { id: "Ingreso", name: "Ingreso" },
            ]}
            disabled={isEdit}
          />
          <DateInput
            source="date"
            label="Fecha"
            parse={(value) => {
              if (!value) return null;
              return moment(value, "YYYY-MM-DD").toDate(); // devuelve Date
            }}
            format={(value) => {
              if (!value) return null;
              return moment(value).format("YYYY-MM-DD"); // muestra en input
            }}
          />
          <ReferenceInput
            label="Sucursal"
            source="branch"
            reference="enterprise/branches"
            filter={{
              status: true,
            }}
          >
            <SelectBranchInput source="branch" />
          </ReferenceInput>
          <ReferenceInput
            label="Empleado"
            source="employee"
            reference={"employee/provider/" + providerId}
            filter={{
              idProv: providerId,
              state: true,
            }}
            validate={false}
          >
            <SelectEmployeeInput source="employee" />
          </ReferenceInput>
          <TextInput source="description" label="Descripción" />
          <NumberInput source="amount" label="Monto (Bs.)" />
          <NumberInput source="quantity" label="Cantidad" />
          <SelectInput
            source="paymentMethod"
            label="Método de Pago"
            choices={Object.values(PAYMENT_METHODS).map((method) => ({
              id: method,
              name: method,
            }))}
            required
          />
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
const BookingEditDialog = ({ open, handleClose, booking, onSuccess, onDelete }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [servicesCart, setServicesCart] = useState([]);
  const [allServices, setAllServices] = useState([]);

  // Inicializar servicios cuando se abre el dialog
  useEffect(() => {
    if (booking && open) {
      setServicesCart(booking.serviceCart || []);
    }
  }, [booking, open]);

  // Cargar todos los servicios disponibles
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data } = await dataProvider.getList("service", {
          pagination: { page: 1, perPage: 1000 },
          filter: { enabled: true },
        });
        setAllServices(data);
      } catch (error) {
        // Error loading services
      }
    };

    if (open && booking) {
      fetchServices();
    }
  }, [open, booking, dataProvider]);

  const handleAddService = () => {
    // Agregar un servicio por defecto si no hay ninguno seleccionado
    const serviceToAdd = allServices.length > 0 ? allServices[0] : null;
    if (!serviceToAdd) return;

    const newServiceItem = {
      service: {
        _id: serviceToAdd._id,
        name: serviceToAdd.name,
        unitPrice: serviceToAdd.unitPrice,
        unitEstimatedWorkMinutes: serviceToAdd.unitEstimatedWorkMinutes,
      },
      quantity: 1,
      price: serviceToAdd.unitPrice,
      unitEstimatedWorkMinutes: serviceToAdd.unitEstimatedWorkMinutes,
    };

    setServicesCart([...servicesCart, newServiceItem]);
  };

  // const handleRemoveService = () => {
  //   if (selectedRemoveService === null) return;

  //   const newCart = servicesCart.filter((_, index) => index !== selectedRemoveService);
  //   setServicesCart(newCart);
  //   setSelectedRemoveService(null);
  // };

  const updateServiceQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) return;

    const updatedCart = [...servicesCart];
    updatedCart[index].quantity = newQuantity;
    updatedCart[index].price = updatedCart[index].service.unitPrice * newQuantity;
    setServicesCart(updatedCart);
  };

  const calculateTotal = () => {
    return servicesCart.reduce((total, item) => total + item.price, 0);
  };

  const calculateEmployeeCommission = () => {
    return servicesCart.reduce((total, item) => {
      const commissionEmployee = item.service.commissionEmployee || 0;
      const serviceTotal = item.price;
      return total + (serviceTotal * commissionEmployee) / 100;
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("YYYY-MM-DDTHH:mm");
  };

  const handleEditSave = async (values) => {
    // Validar que haya al menos un servicio en el carrito
    if (!servicesCart || servicesCart.length === 0) {
      notify("Error: Debe tener al menos un servicio en la reserva", { type: "error" });
      return;
    }

    try {
      // Formatear la fecha de booking
      const bookingDate = values.bookingDate ? new Date(values.bookingDate) : booking.bookingDate;

      const dataToSend = {
        bookingDate,
        totalEstimatedWorkMinutes: values.totalEstimatedWorkMinutes || booking.totalEstimatedWorkMinutes,
        status: values.status || booking.status,
        notes: values.notes || booking.notes,
        paymentInfo: {
          ...booking.paymentInfo,
          totalPrice: values.totalPrice || calculateTotal(),
          paymentMethod: values.paymentMethod || booking.paymentInfo?.paymentMethod,
          partialPayment: values.partialPayment || booking.paymentInfo?.partialPayment || 0,
          status: values.paymentStatus || booking.paymentInfo?.status,
          feeAmountEmployee: values.feeAmountEmployee || calculateEmployeeCommission(),
        },
        // Incluir servicios editados
        serviceCart: servicesCart,
        // Mantener otros campos importantes del booking original
        employee: booking.employee,
        customer: booking.customer,
        provider: booking.provider,
        branch: booking.branch,
        isInBranch: booking.isInBranch,
      };

      await dataProvider.update("booking", { id: booking._id, data: dataToSend });
      notify("Reserva actualizada con éxito", { type: "success" });
      handleClose();
      onSuccess();
    } catch (error) {
      notify("Error al actualizar la reserva", { type: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await dataProvider.delete("booking", { id: booking._id });
      notify("Reserva eliminada con éxito", { type: "success" });
      handleClose();
      setConfirmDeleteOpen(false);
      onDelete();
    } catch (error) {
      notify("Error al eliminar la reserva", { type: "error" });
    }
  };

  if (!booking) return null;

  const BOOKING_STATUS_OPTIONS = [
    { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
    { id: "Cotizacion enviada", name: "Cotización enviada" },
    { id: "Pendiente", name: "Pendiente" },
    { id: "Confirmado", name: "Confirmado" },
    { id: "En proceso", name: "En proceso" },
    { id: "Finalizado", name: "Finalizado" },
    { id: "Cancelada por el proveedor", name: "Cancelada por el proveedor" },
    { id: "Cancelada por el cliente", name: "Cancelada por el cliente" },
    { id: "Caducado", name: "Caducado" },
  ];

  const PAYMENT_STATUS_OPTIONS = [
    { id: "Pendiente", name: "Pendiente" },
    { id: "Pagado", name: "Pagado" },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            Editar Reserva #{booking._id}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <SimpleForm
            record={{
              ...booking,
              bookingDate: formatDate(booking.bookingDate),
              totalPrice: booking.paymentInfo?.totalPrice,
              paymentMethod: booking.paymentInfo?.paymentMethod,
              partialPayment: booking.paymentInfo?.partialPayment,
              paymentStatus: booking.paymentInfo?.status,
              feeAmountEmployee: booking.paymentInfo?.feeAmountEmployee,
            }}
            onSubmit={handleEditSave}
            toolbar={
              <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: 0 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{ py: 1.5, color: "white" }}
                    disabled={!servicesCart || servicesCart.length === 0}
                  >
                    Guardar Cambios
                  </Button>
                  <Button onClick={handleClose} color="inherit">
                    Cancelar
                  </Button>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmDeleteOpen(true)}
                  startIcon={<DeleteIcon />}
                >
                  Eliminar Reserva
                </Button>
              </Toolbar>
            }
          >
            {/* Componente para actualización automática de campos */}
            <AutoUpdateFields
              servicesCart={servicesCart}
              calculateTotal={calculateTotal}
              calculateEmployeeCommission={calculateEmployeeCommission}
            />

            {/* Información básica del booking */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
              Información de la Reserva
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                <DateTimeInput source="bookingDate" label="Fecha y hora de la reserva" sx={{ flex: 1 }} disabled />
                <NumberInput
                  source="totalEstimatedWorkMinutes"
                  label="Duración estimada (minutos)"
                  sx={{ flex: 1 }}
                  min={1}
                />
              </Box>

              <SelectInput
                source="status"
                label="Estado de la reserva"
                choices={BOOKING_STATUS_OPTIONS}
                sx={{ flex: 1 }}
              />
            </Box>

            <TextInput source="notes" label="Notas" multiline rows={3} fullWidth sx={{ mb: 2 }} />

            {/* Información de pago */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "primary.main" }}>
              Información de Pago
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <NumberInput source="totalPrice" label="Precio total (Bs.)" sx={{ flex: 1 }} min={0} />
              <NumberInput source="feeAmountEmployee" label="Comisión empleado (Bs.)" sx={{ flex: 1 }} min={0} />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <SelectInput
                source="paymentMethod"
                label="Método de pago"
                choices={Object.values(PAYMENT_METHODS).map((method) => ({
                  id: method,
                  name: method,
                }))}
                sx={{ flex: 1 }}
              />
              <SelectInput
                source="paymentStatus"
                label="Estado de pago"
                choices={PAYMENT_STATUS_OPTIONS}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Sección de edición de servicios */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "primary.main" }}>
              Servicios Solicitados
            </Typography>

            {/* Mensaje de advertencia cuando no hay servicios */}
            {(!servicesCart || servicesCart.length === 0) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
                <Typography color="warning.dark" sx={{ fontWeight: "bold" }}>
                  ⚠️ Advertencia: Debe tener al menos un servicio para guardar la reserva
                </Typography>
              </Box>
            )}

            {/* Lista de servicios seleccionados */}
            {servicesCart.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <ServiceSelector
                      service={item.service}
                      allServices={allServices}
                      onServiceChange={(selectedService, serviceIndex) => {
                        const updatedCart = [...servicesCart];
                        updatedCart[serviceIndex] = {
                          ...updatedCart[serviceIndex],
                          service: selectedService,
                          price: selectedService.unitPrice * updatedCart[serviceIndex].quantity,
                        };
                        setServicesCart(updatedCart);
                      }}
                      index={index}
                    />
                  </Box>

                  {/* Controles de cantidad */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<span>-</span>}
                      sx={{ color: "white" }}
                      onClick={() => updateServiceQuantity(index, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </Button>
                    <Typography sx={{ minWidth: 30, textAlign: "center" }}>{item.quantity}</Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<span>+</span>}
                      sx={{ color: "white" }}
                      onClick={() => updateServiceQuantity(index, item.quantity + 1)}
                    >
                      +
                    </Button>
                  </Box>

                  {/* Botón eliminar */}
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      const updatedCart = servicesCart.filter((_, i) => i !== index);
                      setServicesCart(updatedCart);
                    }}
                    size="small"
                  >
                    ✕
                  </Button>
                </Box>

                {/* Información del servicio */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  }}
                >
                  <span>
                    Precio unitario: Bs.{" "}
                    {parseFloat(item.service.unitPrice) % 1 === 0
                      ? parseFloat(item.service.unitPrice).toString()
                      : parseFloat(item.service.unitPrice).toFixed(2)}
                  </span>
                  <span>Subtotal: Bs. {item.price % 1 === 0 ? item.price.toString() : item.price.toFixed(2)}</span>
                </Box>
              </Box>
            ))}

            {/* Totales calculados automáticamente */}
            {/* {servicesCart.length > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: "info.light", borderRadius: 1, color: "white" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Cálculos Automáticos (se actualizan en tiempo real):
                </Typography>
                <Typography variant="body1">Total de servicios: Bs. {calculateTotal().toFixed(2)}</Typography>
                <Typography variant="body1">
                  Comisión para empleado: Bs. {calculateEmployeeCommission().toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: "block", opacity: 0.9 }}>
                  Estos valores se aplican automáticamente a los campos de arriba
                </Typography>
              </Box>
            )} */}

            {/* Botón para agregar nuevo servicio */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddService}
                // startIcon={<span>+</span>}
                fullWidth
                sx={{ color: "white" }}
              >
                Agregar Servicio +
              </Button>
            </Box>

            {/* Total de servicios */}
            {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total: Bs. {calculateTotal().toFixed(2)}
              </Typography>
            </Box> */}

            {/* Información del cliente (solo lectura) */}
            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>
              Información del Cliente
            </Typography>

            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mb: 2 }}>
              <Typography>
                <strong>Nombre:</strong> {booking.customer?.fullName}
              </Typography>
              <Typography>
                <strong>Teléfono:</strong> {booking.customer?.phone}
              </Typography>
              {booking.customer?.email && (
                <Typography>
                  <strong>Email:</strong> {booking.customer?.email}
                </Typography>
              )}
              {booking.customer?.address?.direction && (
                <Typography>
                  <strong>Dirección:</strong> {booking.customer?.address?.direction}
                </Typography>
              )}
            </Box>

            {/* Información del empleado (solo lectura) */}
            {booking.employee && (
              <>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
                  Empleado Asignado
                </Typography>
                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography>
                    <strong>Nombre:</strong> {booking.employee?.fullName}
                  </Typography>
                  <Typography>
                    <strong>Teléfono:</strong> {booking.employee?.phone}
                  </Typography>
                </Box>
              </>
            )}

            {/* Servicios (solo lectura) */}
            {/* <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
              Servicios Solicitados
            </Typography>
            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              {booking.serviceCart?.map((service, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography>
                    <strong>{service.name}</strong>
                  </Typography>
                  <Typography color="text.secondary">
                    Cantidad: {service.quantity} | Precio: {formatCurrency(service.price)}
                  </Typography>
                </Box>
              ))}
            </Box> */}
          </SimpleForm>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta reserva? Esta acción no se puede deshacer.</Typography>
          <Typography sx={{ mt: 1, fontWeight: "bold" }}>
            Reserva #{booking._id} - {booking.customer?.fullName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar Reserva
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ListActions = ({ onCreate, isFreemium, onUpgradeClick }) => {
  const { updateFilter, clearFilters } = useFilterContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const profile = JSON.parse(localStorage.getItem("profile"));
  const [filters, setFilters] = useState({
    bookingDate: { from: "", to: "" },
    branch: "",
    "branch._id": profile?.role === "admin-sucursal" ? profile?.branch?._id || "" : "",
    employee: "",
    paymentMethod: "",
  });
  console.log(filters);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { permissions } = usePermissions();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (filter, value) => {
    setFilters((prev) => ({
      ...prev,
      [filter]: value,
    }));

    // Si se cambia la sucursal, limpiar el empleado seleccionado
    if (filter === "branch._id") {
      setFilters((prev) => ({
        ...prev,
        employee: "",
      }));
      // updateFilter("employee._id", null);
    }

    if (value) {
      updateFilter(filter, value);
    } else {
      updateFilter(filter, null);
    }
  };

  const PAYMENT_METHODS = Object.freeze({
    CASH: "Efectivo",
    CARD: "Tarjeta",
    QR: "QR",
    TIGO_MONEY: "Tigo Money",
    COURTESY: "Cortesia",
    GIFT_CARD: "Gift Card",
  });

  return (
    <TopToolbar>
      <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
        <CreateButton
          // onClick={onCreate}
          onClick={isFreemium ? onUpgradeClick : onCreate}
          label={"Crear Transacción"}
          // disabled={isFreemium}
          style={{
            fontSize: "12px",
          }}
        />
        <Button
          // onClick={handleClick}
          onClick={isFreemium ? onUpgradeClick : handleClick}
          style={{
            minWidth: 40,
            padding: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FilterListIcon style={{ marginRight: isSmallScreen ? 0 : 8 }} />
          {!isSmallScreen && (
            <Typography variant="button" style={{ fontSize: "12px" }}>
              Añadir Filtro
            </Typography>
          )}
        </Button>
        <ExportButton
          filename="balance.xlsx"
          label={isSmallScreen ? "" : "Exportar"}
          filters={filters}
          size="large"
          isFreemium={isFreemium}
          onUpgradeClick={onUpgradeClick}
        />
      </div>
      {!isFreemium && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem>
            <DateInput
              label="Desde"
              source="bookingDate>"
              value={filters.bookingDate.from}
              onChange={(e) => handleFilterChange("bookingDate>", e.target.value)}
            />
          </MenuItem>
          <MenuItem>
            <DateInput
              label="Hasta"
              source="bookingDate<"
              value={filters.bookingDate.to}
              onChange={(e) => handleFilterChange("bookingDate<", e.target.value)}
            />
          </MenuItem>
          {permissions !== "admin-sucursal" && (
            <MenuItem>
              <ReferenceInput label="Sucursal" source="branch.name" reference="enterprise/branches">
                <SelectInput
                  label="Sucursal"
                  value={filters.branch}
                  optionText={(value) => value.name}
                  onChange={(e) => handleFilterChange("branch._id", e.target.value)}
                />
              </ReferenceInput>
            </MenuItem>
          )}
          <MenuItem>
            <SelectInput
              label="Método de Pago"
              source="paymentMethod"
              value={filters.paymentMethod}
              choices={Object.values(PAYMENT_METHODS).map((name) => ({ id: name, name }))}
              onChange={(e) => handleFilterChange("paymentInfo.paymentMethod", e.target.value)}
            />
          </MenuItem>
          <MenuItem>
            <ReferenceInput
              label="Empleado"
              source="employee"
              reference="employeesAll"
              filter={{
                providerId:
                  permissions !== "admin-sucursal"
                    ? JSON.parse(localStorage.getItem("profile"))._id
                    : JSON.parse(localStorage.getItem("profile")).employer._id,
                branchUrl: filters["branch._id"] || "",
                state: true,
              }}
            >
              <SelectInput
                label="Empleado"
                value={filters.employee}
                optionText={(value) => `${value.first_name} ${value.last_name}`}
                onChange={(e) => handleFilterChange("employee._id", e.target.value)}
                disabled={!filters["branch._id"]}
                helperText={!filters["branch._id"] ? "Primero selecciona una sucursal" : ""}
              />
            </ReferenceInput>
          </MenuItem>

          <MenuItem>
            <Button
              onClick={() => {
                clearFilters();
                setAnchorEl(null);
                window.location.reload();
              }}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Typography variant="body1" style={{ marginRight: 8 }}>
                Limpiar filtros
              </Typography>
            </Button>
          </MenuItem>
        </Menu>
      )}
    </TopToolbar>
  );
};

const mapFiltersByResource = (filters, resource) => {
  const mapped = { ...filters };

  // Solo mapeamos las fechas si es para transacciones
  if (resource.includes("transaction/provider")) {
    if (filters["bookingDate>"]) {
      mapped["date>"] = filters["bookingDate>"];
      delete mapped["bookingDate>"];
    }
    if (filters["bookingDate<"]) {
      mapped["date<"] = filters["bookingDate<"];
      delete mapped["bookingDate<"];
    }
    // Mapeamos paymentMethod si existe en los filtros
    if (filters["paymentInfo.paymentMethod"]) {
      mapped["paymentMethod"] = filters["paymentInfo.paymentMethod"];
      delete mapped["paymentInfo.paymentMethod"];
    }
  }

  return mapped;
};

// Componente para el menú de acciones de cada fila
const BookingActionsMenu = ({ booking, isFreemium, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    onEdit(booking);
    handleClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    onDelete(booking);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disabled={isFreemium}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>
    </>
  );
};

export const BalanceList = () => {
  // Estados para el dialog de edición del booking
  const [bookingEditOpen, setBookingEditOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);

  // Estados para confirmación de eliminación desde la tabla
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  // Estados para el dialog de edición de transacciones
  const [transactionEditOpen, setTransactionEditOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // Estados para el dialog de crear transacciones
  const [transactionCreateOpen, setTransactionCreateOpen] = useState(false);

  // Estados para el dialog de edición de comisiones
  const [commissionEditOpen, setCommissionEditOpen] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);

  // Estados para eliminación directa de transacciones
  const [transactionDeleteOpen, setTransactionDeleteOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Estados para eliminación directa de comisiones
  const [commissionDeleteOpen, setCommissionDeleteOpen] = useState(false);
  const [commissionToDelete, setCommissionToDelete] = useState(null);

  const [ingresosTotal, setIngresosTotal] = useState(0);
  const [otrosIngresosTotal, setOtrosIngresosTotal] = useState(0);
  const [egresosTotal, setEgresosTotal] = useState(0);
  const [otrosEgresosTotal, setOtrosEgresosTotal] = useState(0);
  const [egresosEmpleado, setEgresosEmpleado] = useState(0);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { filters } = useFilterContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const ingresosTotales = ingresosTotal + otrosIngresosTotal;
  const egresosTotales = egresosTotal + otrosEgresosTotal + egresosEmpleado;
  const total = ingresosTotales - egresosTotales;

  const refresh = useRefresh();

  useEffect(() => {
    const interval = setInterval(() => refresh(), 10000);
    return () => {
      clearInterval(interval);
    };
  }, [refresh]);

  // Handlers para las acciones del booking
  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setBookingEditOpen(true);
  };

  const handleDeleteBooking = (booking) => {
    setBookingToDelete(booking);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteBooking = async () => {
    try {
      await dataProvider.delete("booking", { id: bookingToDelete._id });
      notify("Reserva eliminada con éxito", { type: "success" });
      setDeleteConfirmOpen(false);
      setBookingToDelete(null);
      refresh(); // Refrescar la lista
    } catch (error) {
      notify("Error al eliminar la reserva", { type: "error" });
      setDeleteConfirmOpen(false);
      setBookingToDelete(null);
    }
  };

  const cancelDeleteBooking = () => {
    setDeleteConfirmOpen(false);
    setBookingToDelete(null);
  };

  const handleCloseBookingEdit = () => {
    setBookingEditOpen(false);
    setEditingBooking(null);
  };

  const handleBookingEditSuccess = () => {
    refresh();
    setRefreshTrigger((prev) => prev + 1);
    handleCloseBookingEdit();
  };

  // Handlers para las transacciones
  const handleCreateTransaction = () => {
    setTransactionCreateOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionEditOpen(true);
  };

  const handleCloseTransactionCreate = () => {
    setTransactionCreateOpen(false);
  };

  const handleCloseTransactionEdit = () => {
    setTransactionEditOpen(false);
    setEditingTransaction(null);
  };

  const handleTransactionCreateSuccess = () => {
    refresh();
    setRefreshTrigger((prev) => prev + 1);
    handleCloseTransactionCreate();
  };

  const handleTransactionEditSuccess = () => {
    refresh();
    setRefreshTrigger((prev) => prev + 1);
    handleCloseTransactionEdit();
  };

  const handleTransactionDelete = () => {
    refresh();
    handleCloseTransactionEdit();
  };

  // Handlers para las comisiones
  const handleEditCommission = (booking) => {
    setEditingCommission(booking);
    setCommissionEditOpen(true);
  };

  const handleCloseCommissionEdit = () => {
    setCommissionEditOpen(false);
    setEditingCommission(null);
  };

  const handleCommissionEditSuccess = () => {
    refresh();
    setRefreshTrigger((prev) => prev + 1);
    handleCloseCommissionEdit();
  };

  const handleCommissionDelete = () => {
    refresh();
    setRefreshTrigger((prev) => prev + 1);
    handleCloseCommissionEdit();
  };

  // Handlers para eliminación directa de transacciones
  const handleDeleteTransaction = (transaction) => {
    setTransactionToDelete(transaction);
    setTransactionDeleteOpen(true);
  };

  const confirmDeleteTransaction = async () => {
    try {
      await dataProvider.delete("transaction", { id: transactionToDelete._id });
      notify("Transacción eliminada con éxito", { type: "success" });
      setTransactionDeleteOpen(false);
      setTransactionToDelete(null);
      refresh();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      notify("Error al eliminar la transacción", { type: "error" });
      setTransactionDeleteOpen(false);
      setTransactionToDelete(null);
    }
  };

  const cancelDeleteTransaction = () => {
    setTransactionDeleteOpen(false);
    setTransactionToDelete(null);
  };

  // Handlers para eliminación directa de comisiones
  const handleDeleteCommission = (booking) => {
    setCommissionToDelete(booking);
    setCommissionDeleteOpen(true);
  };

  const confirmDeleteCommission = async () => {
    try {
      await dataProvider.delete("booking", { id: commissionToDelete._id });
      notify("Reserva eliminada con éxito", { type: "success" });
      setCommissionDeleteOpen(false);
      setCommissionToDelete(null);
      refresh();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      notify("Error al eliminar la reserva", { type: "error" });
      setCommissionDeleteOpen(false);
      setCommissionToDelete(null);
    }
  };

  const cancelDeleteCommission = () => {
    setCommissionDeleteOpen(false);
    setCommissionToDelete(null);
  };

  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

  const { data: provider } = useGetOne("providers", { id: providerId });
  const isFreemium = provider?.plan === "freemium";
  return (
    <Container style={{ margin: 16, padding: 16, marginTop: 0, paddingTop: 0 }}>
      <Form>
        <ListActions
          onCreate={handleCreateTransaction}
          isFreemium={isFreemium}
          onUpgradeClick={() => setOpenUpgradeModal(true)}
        />
      </Form>
      <List
        sort={{ field: "bookingDate", order: "DESC" }}
        resource="booking/balance"
        title=" "
        exporter={false}
        actions={false}
        filter={filters}
        perPage={10}
        disableSyncWithLocation
        empty={
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <h2>No hay ingresos por servicios disponibles</h2>
          </div>
        }
      >
        <Box sx={{ margin: 0, padding: 0, borderBottom: "1px solid #ddd" }}>
          <Typography variant="h5" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
            Ingresos por Servicios
          </Typography>
        </Box>
        {isFreemium && (
          <UpgradeStickyBanner
            planActual="Freemium"
            planDestino="Standard"
            modulo="Balances"
            onClickUpgrade={() => setOpenUpgradeModal(true)}
          />
        )}
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={{
            pointerEvents: isFreemium ? "none" : "auto",
            opacity: isFreemium ? 0.5 : 1,
            "& .RaDatagrid-headerCell": {
              maxWidth: "100px",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-cell": {
              textAlign: "center",
            },
          }}
        >
          <TextField source="_id" label="ID" />
          <ArrayField source="serviceCart" label="Servicios Solicitados">
            <SingleFieldList>
              <ChipField sx={{ display: "flex", flexWrap: "wrap", maxWidth: 200 }} source="service.name" />
            </SingleFieldList>
          </ArrayField>
          <DateField
            source="bookingDate"
            label="Fecha de Inicio"
            locales="es-ES"
            options={{ day: "2-digit", month: "2-digit", year: "numeric" }}
          />
          <FunctionField
            source="paymentInfo.totalPrice"
            label="Precio Total (Bs.)"
            render={(record) => {
              if (!record.paymentInfo?.totalPrice) return "0";
              const num = parseFloat(record.paymentInfo.totalPrice);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          {/* <TextField source="paymentInfo.partialPayment" label="Total Pagos Parciales (Bs.)" /> */}
          <TextField source="employee.fullName" label="Empleado" />
          <TextField source="paymentInfo.paymentMethod" label="Método de Pago" />
          <TextField source="paymentInfo.status" label="Estado de Pago" />
          <TextField source="branch.name" label="Sucursal" />
          <FunctionField
            label="Acciones"
            render={(record) => (
              <BookingActionsMenu
                booking={record}
                isFreemium={isFreemium}
                onEdit={handleEditBooking}
                onDelete={handleDeleteBooking}
              />
            )}
          />
        </Datagrid>
        <InlineBookingTotal setTotal={setIngresosTotal} filters={filters} refreshTrigger={refreshTrigger} />
      </List>

      <List
        sort={{ field: "date", order: "DESC" }}
        resource="transaction/provider/income"
        title=" "
        exporter={false}
        actions={false}
        filter={mapFiltersByResource(filters, "transaction/provider/income")}
        perPage={10}
        disableSyncWithLocation
        empty={
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <h2>No hay otros ingresos disponibles</h2>
          </div>
        }
      >
        <Box sx={{ margin: 0, padding: 0, borderBottom: "1px solid #ddd" }}>
          <Typography variant="h5" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
            Otros Ingresos
          </Typography>
        </Box>
        {isFreemium && (
          <UpgradeStickyBanner
            planActual="Freemium"
            planDestino="Standard"
            modulo="Balances"
            onClickUpgrade={() => setOpenUpgradeModal(true)}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          sx={{
            pointerEvents: isFreemium ? "none" : "auto",
            opacity: isFreemium ? 0.5 : 1,
            "& .RaDatagrid-headerCell": {
              maxWidth: "100px",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-cell": {
              textAlign: "center",
            },
          }}
        >
          <TextField source="description" label="Descripción" />
          <DateField
            source="date"
            label="Fecha"
            locales="es-ES"
            options={{ day: "2-digit", month: "2-digit", year: "numeric" }}
          />
          <TextField source="quantity" label="Cantidad" />
          <FunctionField
            source="amount"
            label="Monto (Bs.)"
            render={(record) => {
              if (!record.amount) return "0";
              const num = parseFloat(record.amount);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          <TextField source="employee.fullName" label="Empleado" />
          <TextField source="paymentMethod" label="Método de Pago" />
          <TextField source="branch.name" label="Sucursal" />
          <FunctionField
            label="Acciones"
            render={(record) => (
              <BookingActionsMenu
                booking={record}
                isFreemium={isFreemium}
                onEdit={() => handleEditTransaction(record)}
                onDelete={() => handleDeleteTransaction(record)}
              />
            )}
          />
        </Datagrid>
        <TotalAmount
          resource="transaction/provider/income"
          setTotal={setOtrosIngresosTotal}
          showInline
          filters={mapFiltersByResource(filters, "transaction/provider/income")}
          refreshTrigger={refreshTrigger}
        />
      </List>
      <List
        sort={{ field: "bookingDate", order: "DESC" }}
        resource="booking/balance"
        title=" "
        exporter={false}
        actions={false}
        filter={filters}
        perPage={10}
        disableSyncWithLocation
        empty={
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <h2>No hay egresos por servicios disponibles</h2>
          </div>
        }
      >
        <Box sx={{ margin: 0, padding: 0, borderBottom: "1px solid #ddd" }}>
          <Typography variant="h5" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
            Egresos por Comisiones de Servicios
          </Typography>
        </Box>
        {isFreemium && (
          <UpgradeStickyBanner
            planActual="Freemium"
            planDestino="Standard"
            modulo="Balances"
            onClickUpgrade={() => setOpenUpgradeModal(true)}
          />
        )}
        <Datagrid
          rowStyle={postRowStyle}
          bulkActionButtons={false}
          sx={{
            pointerEvents: isFreemium ? "none" : "auto",
            opacity: isFreemium ? 0.5 : 1,
            "& .RaDatagrid-headerCell": {
              maxWidth: "100px",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-cell": {
              textAlign: "center",
            },
          }}
        >
          <TextField source="_id" label="ID" />
          <ArrayField source="serviceCart" label="Servicios Solicitados">
            <SingleFieldList>
              <ChipField sx={{ display: "flex", flexWrap: "wrap", maxWidth: 200 }} source="service.name" />
            </SingleFieldList>
          </ArrayField>
          <DateField
            source="bookingDate"
            label="Fecha de Inicio"
            locales="es-ES"
            options={{ day: "2-digit", month: "2-digit", year: "numeric" }}
          />
          <FunctionField
            source="paymentInfo.totalPrice"
            label="Precio Total (Bs.)"
            render={(record) => {
              if (!record.paymentInfo?.totalPrice) return "0";
              const num = parseFloat(record.paymentInfo.totalPrice);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          <TextField source="employee.fullName" label="Empleado" />
          <FunctionField
            source="paymentInfo.feeAmountEmployee"
            label="Comision para Empleado (Bs.)"
            render={(record) => {
              if (!record.paymentInfo?.feeAmountEmployee) return "0";
              const num = parseFloat(record.paymentInfo.feeAmountEmployee);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          <TextField source="paymentInfo.feeRate" label="Tasa de Comisiones (%)" />
          <FunctionField
            source="paymentInfo.feeAmount"
            label="Comisiones (Bs.)"
            render={(record) => {
              if (!record.paymentInfo?.feeAmount) return "0";
              const num = parseFloat(record.paymentInfo.feeAmount);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          <BooleanField source="paymentInfo.isFeePaid" label="Comisión pagada" />
          <TextField source="branch.name" label="Sucursal" />
          <FunctionField
            label="Acciones"
            render={(record) => (
              <BookingActionsMenu
                booking={record}
                isFreemium={isFreemium}
                onEdit={() => handleEditCommission(record)}
                onDelete={() => handleDeleteCommission(record)}
              />
            )}
          />
        </Datagrid>
        <TotalAmount
          resource="booking/balance"
          setTotal={setEgresosTotal}
          setEgresosEmpleado={setEgresosEmpleado}
          showInline
          totalType="egresos"
          filters={filters}
          refreshTrigger={refreshTrigger}
        />
      </List>
      <List
        sort={{ field: "date", order: "DESC" }}
        resource="transaction/provider/expense"
        title=" "
        exporter={false}
        actions={false}
        filter={mapFiltersByResource(filters, "transaction/provider/expense")}
        perPage={10}
        disableSyncWithLocation
        empty={
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <h2>No hay otros egresos disponibles</h2>
          </div>
        }
      >
        <Box sx={{ margin: 0, padding: 0, borderBottom: "1px solid #ddd" }}>
          <Typography variant="h5" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
            Otros Egresos
          </Typography>
        </Box>
        {isFreemium && (
          <UpgradeStickyBanner
            planActual="Freemium"
            planDestino="Standard"
            modulo="Balances"
            onClickUpgrade={() => setOpenUpgradeModal(true)}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          sx={{
            pointerEvents: isFreemium ? "none" : "auto",
            opacity: isFreemium ? 0.5 : 1,
            "& .RaDatagrid-headerCell": {
              maxWidth: "100px",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-cell": {
              textAlign: "center",
            },
          }}
        >
          <TextField source="description" label="Descripción" />
          <DateField
            source="date"
            label="Fecha"
            locales="es-ES"
            options={{ day: "2-digit", month: "2-digit", year: "numeric" }}
          />
          <TextField source="quantity" label="Cantidad" />
          <FunctionField
            source="amount"
            label="Monto (Bs.)"
            render={(record) => {
              if (!record.amount) return "0";
              const num = parseFloat(record.amount);
              return num % 1 === 0 ? num.toString() : num.toFixed(2);
            }}
          />
          <TextField source="employee.fullName" label="Empleado" />
          <TextField source="paymentMethod" label="Método de Pago" />
          <TextField source="branch.name" label="Sucursal" />
          <FunctionField
            label="Acciones"
            render={(record) => (
              <BookingActionsMenu
                booking={record}
                isFreemium={isFreemium}
                onEdit={() => handleEditTransaction(record)}
                onDelete={() => handleDeleteTransaction(record)}
              />
            )}
          />
        </Datagrid>
        <TotalAmount
          resource="transaction/provider/expense"
          setTotal={setOtrosEgresosTotal}
          showInline
          filters={mapFiltersByResource(filters, "transaction/provider/expense")}
          refreshTrigger={refreshTrigger}
        />
      </List>
      <div>
        <Paper>
          <Box sx={{ margin: 0, padding: 2, marginTop: 2, borderBottom: "1px solid #ddd" }}>
            <Typography variant="h5">Resumen de Ingresos y Egresos</Typography>
          </Box>
          <Box sx={{ margin: 0, padding: 2, paddingTop: 0, marginTop: 2 }}>
            <Typography variant="body1" sx={{ opacity: isFreemium ? 0.5 : 1 }}>
              Total Ingresos: Bs. {ingresosTotales.toFixed(2)}
            </Typography>
            <Typography variant="body1" sx={{ marginTop: 1, opacity: isFreemium ? 0.5 : 1 }}>
              Total Egresos: Bs. {egresosTotales.toFixed(2)}
            </Typography>
            <Typography variant="body1" sx={{ marginTop: 1, opacity: isFreemium ? 0.5 : 1 }}>
              Total: Bs. {total.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      </div>
      <UpgradePlanModal
        open={openUpgradeModal}
        onClose={() => setOpenUpgradeModal(false)}
        planActual={provider?.plan}
        planDestino="standard"
      />

      {/* Dialog para editar booking */}
      <BookingEditDialog
        open={bookingEditOpen}
        handleClose={handleCloseBookingEdit}
        booking={editingBooking}
        onSuccess={handleBookingEditSuccess}
        onDelete={handleDeleteBooking}
      />

      {/* Dialog de confirmación de eliminación desde tabla */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteBooking}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar esta reserva?
            {bookingToDelete && (
              <>
                <br />
                <br />
                <strong>Cliente:</strong> {bookingToDelete.customer?.fullName || "N/A"}
                <br />
                <strong>Fecha:</strong> {new Date(bookingToDelete.bookingDate).toLocaleDateString()}
                <br />
                <strong>Estado:</strong> {bookingToDelete.status}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteBooking}>Cancelar</Button>
          <Button onClick={confirmDeleteBooking} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar transacciones */}
      <TransactionEditDialog
        open={transactionEditOpen}
        handleClose={handleCloseTransactionEdit}
        transaction={editingTransaction}
        onSuccess={handleTransactionEditSuccess}
        onDelete={handleTransactionDelete}
      />

      {/* Dialog para editar comisiones */}
      <BookingCommissionEditDialog
        open={commissionEditOpen}
        handleClose={handleCloseCommissionEdit}
        booking={editingCommission}
        onSuccess={handleCommissionEditSuccess}
        onDelete={handleCommissionDelete}
      />

      {/* Dialog de confirmación de eliminación directa de transacciones */}
      <Dialog open={transactionDeleteOpen} onClose={cancelDeleteTransaction} aria-labelledby="transaction-delete-title">
        <DialogTitle id="transaction-delete-title">Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar esta transacción?
            {transactionToDelete && (
              <>
                <br />
                <br />
                <strong>Tipo:</strong> {transactionToDelete.type}
                <br />
                <strong>Monto:</strong> Bs. {transactionToDelete.amount}
                <br />
                <strong>Descripción:</strong> {transactionToDelete.description || "Sin descripción"}
                <br />
                <strong>Fecha:</strong> {new Date(transactionToDelete.date).toLocaleDateString()}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteTransaction}>Cancelar</Button>
          <Button onClick={confirmDeleteTransaction} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación directa de comisiones */}
      <Dialog open={commissionDeleteOpen} onClose={cancelDeleteCommission} aria-labelledby="commission-delete-title">
        <DialogTitle id="commission-delete-title">Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar esta reserva?
            {commissionToDelete && (
              <>
                <br />
                <br />
                <strong>Cliente:</strong> {commissionToDelete.customer?.fullName || "N/A"}
                <br />
                <strong>Empleado:</strong> {commissionToDelete.employee?.fullName || "N/A"}
                <br />
                <strong>Fecha:</strong> {new Date(commissionToDelete.bookingDate).toLocaleDateString()}
                <br />
                <strong>Total:</strong> Bs. {commissionToDelete.paymentInfo?.totalPrice || 0}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteCommission}>Cancelar</Button>
          <Button onClick={confirmDeleteCommission} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear transacción */}
      <TransactionDialog
        open={transactionCreateOpen}
        handleClose={handleCloseTransactionCreate}
        record={null}
        isEdit={false}
        onSuccess={handleTransactionCreateSuccess}
      />

      {/* Dialog para editar transacción */}
      <TransactionEditDialog
        open={transactionEditOpen}
        handleClose={handleCloseTransactionEdit}
        transaction={editingTransaction}
        onSuccess={handleTransactionEditSuccess}
        onDelete={handleTransactionDelete}
      />

      {/* Dialog para editar comisión */}
      <BookingCommissionEditDialog
        open={commissionEditOpen}
        handleClose={handleCloseCommissionEdit}
        booking={editingCommission}
        onSuccess={handleCommissionEditSuccess}
        onDelete={handleCommissionDelete}
      />
    </Container>
  );
};

const SelectBranchInput = ({ source, ...props }) => {
  const choicesContext = useChoicesContext();
  const [searchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();

  React.useEffect(() => {
    const profile = JSON.parse(localStorage.getItem("profile"));
    const id = profile?.branch?._id;

    dataProvider
      .getOne("enterprise/branches", { id })
      .then((branch) => {
        if (profile?.role === "admin-sucursal" && branch.data) {
          setValue("branch", branch.data);
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [setValue]);

  return permissions !== "admin-sucursal" ? (
    <SelectInput
      {...props}
      label="Sucursal"
      optionText={(value) => value.name}
      optionValue="_id"
      parse={(value) => choicesContext?.allChoices.find((e) => e._id === value)}
      format={(value) => value?._id}
      validate={[required()]}
      defaultValue={choicesContext?.allChoices.find((e) => e._id === searchParams.get("branch"))}
    />
  ) : (
    <React.Fragment />
  );
};
//select de empleados
const SelectEmployeeInput = ({ source, ...props }) => {
  const choicesContext = useChoicesContext();
  const branch = useFormContext().watch("branch");
  const { setValue } = useFormContext();

  const handleChange = (event) => {
    const selectedEmployee = choicesContext.allChoices.find((e) => e._id === event.target.value);
    setValue("employeeData", selectedEmployee);
  };

  return (
    <SelectInput
      {...props}
      source={source}
      label="Empleado"
      optionText={(employee) => `${employee.first_name} ${employee.last_name}`}
      optionValue="_id"
      choices={branch ? choicesContext.allChoices.filter((employee) => employee.branch?._id === branch._id) : []}
      onChange={handleChange}
    />
  );
};

// Componente que usa el totalAmount que ya viene en la respuesta de la lista
const InlineBookingTotal = ({ setTotal, filters, refreshTrigger }) => {
  const [totalAmount, setTotalAmount] = useState(0);
  const dataProvider = useDataProvider();

  useEffect(() => {
    // Solo hacer la petición una vez usando los mismos filtros que la lista
    const fetchTotal = async () => {
      try {
        const response = await dataProvider.getList("booking/balance", {
          pagination: { page: 1, perPage: 1 }, // Solo necesitamos el total, no los datos
          filter: filters,
        });
        const total = response.totalAmount || 0;
        setTotalAmount(total);
        if (setTotal && typeof setTotal === "function") {
          setTotal(total);
        }
      } catch (error) {
        // En caso de error, usar valores por defecto
        setTotalAmount(0);
        if (setTotal && typeof setTotal === "function") {
          setTotal(0);
        }
      }
    };

    fetchTotal();
  }, [dataProvider, setTotal, filters, refreshTrigger]); // Agregado refreshTrigger

  return (
    <Typography variant="h6" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
      Total: Bs. {totalAmount % 1 === 0 ? totalAmount.toString() : totalAmount.toFixed(2)}
    </Typography>
  );
};

const TotalAmount = ({ resource, setTotal, showInline, totalType, filters, setEgresosEmpleado, refreshTrigger }) => {
  const dataProvider = useDataProvider();
  const [localTotal, setLocalTotal] = useState(0);
  const [precioTotal, setPrecioTotal] = useState(0);
  useEffect(() => {
    const fetchTotalAmount = async () => {
      try {
        const response = await dataProvider.getList(resource, {
          pagination: { page: 1, perPage: 1000 }, // Necesitamos todos los registros para el cálculo correcto
          filter: filters,
        });

        let total = 0;

        if (totalType === "egresos") {
          total = response.totalFeeAmount || 0;
          if (setEgresosEmpleado) {
            setEgresosEmpleado(response.totalFeeAmountEmployee);
          }
        } else if (totalType === "ingresos" && resource === "booking/balance") {
          // Lógica para ingresos por servicios - solo usar precio total
          total = 0;
          if (response.data && Array.isArray(response.data)) {
            response.data.forEach((booking) => {
              if (booking.paymentInfo) {
                // Siempre usar el precio total únicamente
                total += booking.paymentInfo.totalPrice || 0;
              }
            });
          }
        } else {
          total = response.totalAmount || 0;
        }

        setPrecioTotal(response.totalFeeAmountEmployee);
        setTotal(total);
        setLocalTotal(total);
      } catch (error) {
        setTotal(0);
        setLocalTotal(0);
      }
    };

    fetchTotalAmount();
  }, [dataProvider, resource, setTotal, totalType, filters, setEgresosEmpleado, refreshTrigger]);
  return showInline ? (
    totalType === "egresos" ? (
      <div>
        <Typography variant="h6" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
          Total comisiones por empleado: Bs.{" "}
          {parseFloat(precioTotal) % 1 === 0 ? parseFloat(precioTotal).toString() : parseFloat(precioTotal).toFixed(2)}
        </Typography>
        <Typography variant="h6" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
          Total comisiones por plataforma: Bs.{" "}
          {parseFloat(localTotal) % 1 === 0 ? parseFloat(localTotal).toString() : parseFloat(localTotal).toFixed(2)}
        </Typography>
      </div>
    ) : (
      <Typography variant="h6" sx={{ margin: 1, padding: 0, paddingLeft: 1 }}>
        Total: Bs.{" "}
        {parseFloat(localTotal) % 1 === 0 ? parseFloat(localTotal).toString() : parseFloat(localTotal).toFixed(2)}
      </Typography>
    )
  ) : null;
};
